import React, { FC, Suspense, useState, lazy, useEffect, useRef, MutableRefObject } from "react";
import { Platform, Pressable, Modal, View, ActivityIndicator, ImageBackground, TouchableOpacity, ScrollView, Image, StyleSheet, useWindowDimensions } from "react-native";
import { Asset, ImageLibraryOptions, launchCamera, launchImageLibrary } from "react-native-image-picker";
import { ThemeConsumer } from "@rneui/themed";
import { createThumbnail } from "react-native-create-thumbnail";
import Video from "react-native-video";

// redux
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getImageProperty, optimizeImage } from "../../redux/slices/imageGallerySlice";

// services
import PermissionServices from "../services/permissionService";

// shared components
import { ZenLoader, ZenToast, ZenText, ZenIcon, ZenDivider, IconType, ZenRBSheet } from "zen-core-elements";
import { RBSheetDataInterface } from "zen-core-elements/src/elements/zenRBSheet/zenRBSheet";
import { CustomModal } from "./customModal";
import { copyorupload } from "../../redux/slices/productSlice";
import { copyoruploadResponse } from "../../components/products/productList/productModule";
import { appFonts } from "../../themes/appTheme";
import RNFetchBlob from 'react-native-blob-util';

const StoreImageGallery = lazy(() => import('../../components/products/imageGallery/imageGallery'));

export interface ZenRBSheetRef {
    open: () => void;
    close: () => void;
}

export type imageGalleryModel = {
    id: number;
    caption: string;
    fileUrl: string;
    originalImageName: string;
    fileSize: number;
    keywords: string;
    lastModifiedBy: string | null;
    selected: boolean;
    Key?: string,
    thumbnail?: string
};

export interface commonPickerRes extends Asset {
    success?: boolean;
    key?: string;
    thumbnail?: string;
}

interface CommonUploadProps {
    bottomRef: MutableRefObject<ZenRBSheetRef | null>;
    selectedIndex?: any;
    gallery?: {
        isStorageAvailable?: boolean;
        isStoreGallery?: boolean;
        isRemoveProfile?: boolean;
        isVideo?: boolean;
        videoAvail?: boolean;
        sectionName?: string;
        screenName?: string;
        selectionType?: 'single' | 'multi';
    };
    propFn?: (arg: string, response?: commonPickerRes[]) => void;
    needToUpload?: boolean;
    acceptedFormats?: string[];
}

const appTypo = {
    tiny: 10,
    xsmall: 12,
    small: 14,
    regular: 16,
    medium: 18,
    large: 20,
    xlarge: 24,
};

// Local reusable premium button helper using ZenText and ZenIcon
const LocalButton = ({ title, onPress, loading, disabled, style, icon, type = 'Primary', theme }: any) => {
    const isSecondary = type === 'Secondary';
    const buttonBg = isSecondary ? 'transparent' : (theme?.primary || '#005374');
    const borderCol = isSecondary ? (theme?.primary || '#005374') : 'transparent';
    const textCol = isSecondary ? (theme?.primary || '#005374') : (theme?.light || '#ffffff');

    return (
        <TouchableOpacity
            disabled={disabled || loading}
            onPress={onPress}
            style={[
                {
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingVertical: 8,
                    paddingHorizontal: 15,
                    borderRadius: 8,
                    borderWidth: isSecondary ? 1 : 0,
                    borderColor: borderCol,
                    backgroundColor: buttonBg,
                    opacity: disabled ? 0.5 : 1,
                    gap: 8,
                },
                style
            ]}
        >
            {loading ? (
                <ActivityIndicator size="small" color={textCol} />
            ) : (
                <>
                    {icon}
                    <ZenText content={title} style={{ color: textCol, fontFamily: appFonts?.bold, fontSize: 16 }} />
                </>
            )}
        </TouchableOpacity>
    );
};

const CommonImageUpload: FC<CommonUploadProps> = ({
    selectedIndex,
    gallery,
    propFn,
    needToUpload = true,
    acceptedFormats,
    bottomRef,
}) => {
    const dispatch = useAppDispatch();
    const { width, height } = useWindowDimensions();
    const { rows: imagePropertyData } = useAppSelector(state => state?.storeImageGallery?.imagePropertyData?.value?.response) || {}

    // Local configuration with safe defaults
    const [fileSize, setFileSize] = useState<any>({
        imgFormat: ['png', 'jpg', 'gif', 'jpeg', 'heic', 'webp'],
        imgSize: 5, // MB fallback
        imageDimension: 2000,
        videoFormat: ['mp4', 'mov'],
        videoSize: 20, // MB
        videoDimension: 1920,
        videoLength: 30
    });

    useEffect(() => {
        if (imagePropertyData?.length) {
            setFileSize(imagePropertyData[0].additionalField);
            return
        } else {
            dispatch(getImageProperty()).unwrap().then((response) => {
                if (response?.success && response?.response?.rows?.[0]?.additionalField) {
                    const field = response.response.rows[0].additionalField;
                    setFileSize((prev: any) => ({
                        ...prev,
                        imgFormat: field.imgFormat || prev.imgFormat,
                        imgSize: field.imgSize || prev.imgSize,
                        videoSize: field.videoSize || prev.videoSize,
                        imageDimension: field.imageDimension || prev.imageDimension,
                        videoFormat: field.videoFormat || prev.videoFormat,
                        videoDimension: field.videoDimension || prev.videoDimension,
                        videoLength: field.videoLength || prev.videoLength,
                    }));
                }
            }).catch(err => {
                console.log("Error fetching image properties in picker", err);
            });
        }
    }, []);

    // Dummy.tsx Mode States
    const [imageData, setImageData] = useState<commonPickerRes[]>([]);
    const [storeVisible, setStoreVisible] = useState(false);
    const [load, setLoad] = useState(false);
    const [index, setIndex] = useState(0);
    const [showPreview, setShowPreview] = useState(false);

    const isTransitioning = useRef(false);

    //Function which is used to add SQS 
    const addSQSMessage = (imageRes: commonPickerRes[]) => {
        let payload: { isVideo: boolean, height: number | undefined, width: number | undefined, key: string | undefined }[] = []
        imageRes?.map(item => {
            payload?.push({
                "isVideo": gallery?.isVideo ?? false,
                "height": item?.height,
                "width": item?.width,
                "key": item?.key
            })
        })
        dispatch(optimizeImage({ messages: payload })).unwrap().then((res) => {
            if (res.success) {
                console.log('Image SQS message added successfully')
            }
        }).catch((err) => {
            ZenToast({ message: `Image uploading failed! ${err?.graphQLErrors?.[0]?.details ?? ''}`, type: "error" })
            console.log('Error occured while copyorupload', err);
        })
    }

    //Function which is used to write images in Bucket
    const writeImagesInBucket = (data: copyoruploadResponse['result']['signedUrlsResponse'], imageRes: Asset[], isError?: boolean) => {
        const writePromise = data?.map((item, i) => {
            const fileUri = imageRes[i]?.uri ?? '';
            const cleanPath = Platform.OS === 'ios'
                ? decodeURIComponent(fileUri.replace('file://', ''))
                : fileUri.startsWith('file://') ? fileUri.replace('file://', '') : fileUri;

            return RNFetchBlob.fetch(
                'PUT',
                item?.data?.uploadUrl ?? '',
                {
                    'Content-Type': imageRes?.[i]?.type ?? '',
                },
                RNFetchBlob.wrap(cleanPath)
            ).then((response) => {
                const status = response.info().status;
                if (status === 200) {
                    return { ok: true, status };
                } else {
                    throw new Error(`Upload failed with status ${status}`);
                }
            }).catch((err) => {
                console.log(`[S3 Upload] RNFetchBlob error:`, err);
                bottomRef?.current?.close()
                throw err;
            });
        });

        Promise.allSettled(writePromise).then(async (res) => {
            if (res) {
                let imageData = imageRes?.map((item, i) => {
                    const outcome = res[i];
                    const success = outcome?.status === 'fulfilled' && outcome.value && ((outcome.value as any).ok || (outcome.value as any).status === 200);
                    return {
                        ...item,
                        success: !!success,
                        key: data?.[i]?.data?.key
                    };
                });
                const resolvedImages = imageData?.filter(i => i?.success)
                const failedCount = (imageData?.length ?? 0) - (resolvedImages?.length ?? 0);

                if (resolvedImages?.length) {
                    if (gallery?.isVideo) {
                        let thumbnail = null
                        const videoSource = Platform.OS === 'ios' ? decodeURIComponent((imageRes?.[0]?.uri as string)?.replace('file://', '')) : imageRes?.[0]?.uri
                        try {
                            const videoThumbnail = await createThumbnail({ url: videoSource as string, timeStamp: 1000, cacheName: videoSource?.split("/")?.pop() })
                            thumbnail = videoThumbnail?.path
                        }
                        catch (err) {
                            console.log('Error occured while generating video thumbnail', err);
                        }
                        propFn?.("Library", [{ ...imageData?.[0], thumbnail: thumbnail ?? undefined }])
                    }
                    else {
                        propFn?.("Library", resolvedImages)
                    }
                    setImageData([])
                    setLoad(false)
                    addSQSMessage(resolvedImages!)

                    if (failedCount > 0) {
                        ZenToast({ message: `Failed to upload ${failedCount} image(s)`, type: "error" })
                    } else if (!gallery?.isVideo && isError) {
                        ZenToast({ message: 'File format is not supported', type: "error" })
                    }
                }
                else {
                    throw 'Upload failed'
                }
            }
        }).catch((err) => {
            propFn?.('Cancel')
            ZenToast({ message: gallery?.isVideo ? 'Failed to pick video' : 'Image uploading failed!', type: "error" })
            setLoad(false)
            setImageData([])
            console.log('Error occured while uploading images in S3 bucket', err)
        })
    }


    // Mock network operations replacing S3/GraphQL mutations
    const uploadImageInBucket = async (imageRes: Asset[], isVideo = false, isZen = false) => {
        if (imageRes) {
            setLoad(true)
            let validImg = imageRes?.filter(i => (i?.fileSize! < fileSize?.imgSize * Math.pow(1024, 2) && (acceptedFormats ?? fileSize?.imgFormat)?.includes?.(i?.fileName?.split(".")?.pop()!)))
            const imageData = isVideo ? imageRes : validImg
            let payload = imageData?.map((val, i) => ({
                fileIndex: i,
                isVideo: isVideo,
                isZenbasket: isZen,
                sectionName: gallery?.sectionName,
                size: (val?.fileSize! / Math.pow(1024, 2)),
                fileName: val?.fileName,
                extension: val?.fileName?.split('.')?.pop()?.toLowerCase(),
                image: null
            }))
            await dispatch(copyorupload({ payload: { files: payload, isCustomUrl: false } })).unwrap().then((res) => {
                if (res.success) {
                    writeImagesInBucket(res?.result?.signedUrlsResponse, imageData, imageRes?.length != validImg?.length)
                }
            }).catch((err) => {
                setLoad(false)
                setImageData([])
                propFn?.('Cancel')
                ZenToast({ message: gallery?.isVideo ? "Failed to pick video" : `Image uploading failed! ${err?.graphQLErrors?.[0]?.details ?? ''}`, type: "error" })
                console.log('Error occured while copyorupload', err);
            })
        }
    };

    //Function to be called while closing image gallery
    const onCloseGallery = (value: imageGalleryModel[], isStore = false) => {
        if (isStore) {
            let keys = value?.map((i, l) => ({ key: i?.Key, fileName: value?.[l]?.Key, thumbnail: i?.thumbnail }))
            propFn?.("StoreImageGallery", keys)
            setStoreVisible(false)
        }
        else {
            setLoad(true)
            let payload = value?.map((val, i) => ({
                fileIndex: i,
                isVideo: false,
                isZenbasket: true,
                sectionName: gallery?.sectionName,
                size: val?.fileSize ?? null,
                fileName: val?.fileUrl?.split('/')?.pop()?.toLowerCase(),
                extension: val?.fileUrl?.split('.')?.pop()?.toLowerCase(),
                image: val?.fileUrl
            }))
            dispatch(copyorupload({ payload: { isCustomUrl: false, files: payload } })).unwrap().then((res) => {
                if (res.success) {
                    let imageKeys = res?.result?.copyMediaResponse?.map((item, i) => ({ key: item?.data?.uplodedFileUrl, fileName: value?.[i]?.fileUrl }))
                    propFn?.('ZenbasketGallery', imageKeys)
                    setLoad(false)
                }
            }).catch((err) => {
                setLoad(false)
                propFn?.("Cancel")
                ZenToast({ message: `Image uploading failed! ${err?.graphQLErrors?.[0]?.details ?? ''}`, type: "error" })
                console.log('Error occured while copyorupload', err);
            })
        }
    }

    // Open native image/video picker
    const openPicker = (num: number) => {
        const option: ImageLibraryOptions = {
            mediaType: 'photo',
            selectionLimit: selectedIndex?.value?.imagesMaxLength ? (selectedIndex?.value?.imagesMaxLength - imageData?.length) : 1,
            includeBase64: false,

        };

        const videoOption: ImageLibraryOptions = {
            includeBase64: false,
            mediaType: 'video',
        };

        const pickerPromise = num === 1
            ? launchImageLibrary(gallery?.isVideo ? videoOption : option)
            : launchCamera(option);

        pickerPromise.then(res => {
            if (res.didCancel) {
                console.log('User cancelled picker');
                propFn?.('Cancel');
            } else if (res.errorMessage) {
                console.log('Picker Error: ', res.errorMessage);
                propFn?.('Cancel');
                ZenToast({ type: 'error', message: 'Something went wrong' });
            } else if (!imageData?.length && !gallery?.isVideo && res?.assets && res?.assets?.length === 1 && res?.assets?.[0]?.fileSize! > fileSize?.imgSize * Math.pow(1024, 2)) {
                propFn?.('Cancel');
                ZenToast({ message: `Image size exceeds limit of ${fileSize?.imgSize}MB`, type: "error" });
            } else if (!imageData?.length && !gallery?.isVideo && res?.assets && res?.assets?.[0]?.fileName?.split(".")?.pop() && !(acceptedFormats ?? fileSize?.imgFormat)?.includes?.(res?.assets?.[0]?.fileName?.split(".")?.pop()!)) {
                propFn?.('Cancel');
                ZenToast({ message: "Unsupported file format.", type: "error" });
            } else if (!imageData?.length && !gallery?.isVideo && fileSize?.imageDimension && (Math.max(res.assets?.[0]?.width ?? 0, res.assets?.[0]?.height ?? 0) > fileSize?.imageDimension)) {
                propFn?.('Cancel');
                ZenToast({ message: `Image dimension exceeds ${fileSize?.imageDimension}px`, type: "error" });
            } else if (gallery?.isVideo && res?.assets?.[0]) {
                const asset = res.assets?.[0];
                const format = asset?.fileName?.split('.')?.pop()?.toLowerCase();
                if (format && fileSize?.videoFormat && !fileSize?.videoFormat?.includes(format)) {
                    propFn?.('Cancel');
                    ZenToast({ message: "Unsupported file format.", type: "error" });
                } else if (fileSize?.videoSize && (res?.assets?.[0]?.fileSize! > fileSize?.videoSize * Math.pow(1024, 2))) {
                    propFn?.('Cancel');
                    ZenToast({ message: `File size exceeds ${fileSize?.videoSize}MB`, type: "error" });
                } else if (fileSize?.videoDimension && (Math.max(asset?.width ?? 0, asset?.height ?? 0) > fileSize?.videoDimension)) {
                    propFn?.('Cancel');
                    ZenToast({ message: `Video dimension exceeds ${fileSize?.videoDimension}px`, type: "error" });
                } else if (fileSize?.videoLength && ((asset?.duration ?? 0) > fileSize?.videoLength)) {
                    propFn?.('Cancel');
                    ZenToast({ message: `Video duration exceeds ${fileSize?.videoLength} seconds`, type: "error" });
                } else if (needToUpload) {
                    setImageData(prev => [...prev, ...(res?.['assets'] ?? [])]);
                    if (num === 1) {
                        setShowPreview(true);
                    } else {
                        uploadImageInBucket(res['assets'] || [], gallery?.isVideo);
                    }
                } else {
                    propFn?.('Library', res as commonPickerRes[]);
                }
            } else if (needToUpload) {
                setImageData(prev => [...prev, ...(res?.['assets'] ?? [])]);
                if (num === 1) {
                    setShowPreview(true);
                } else {
                    uploadImageInBucket(res['assets'] || [], gallery?.isVideo);
                }
            } else {
                propFn?.('Library', res as commonPickerRes[]);
            }
        });
    };



    // File validation checker
    const isFileValid = (file: commonPickerRes) => {
        const ext = file?.fileName?.split(".")?.pop() || '';
        return (acceptedFormats ?? fileSize?.imgFormat)?.includes?.(ext) && file?.fileSize! <= fileSize?.imgSize * Math.pow(1024, 2);
    };

    return (
        <ThemeConsumer>
            {({ theme }) => {
                const isStorageAllowed = gallery?.isStorageAvailable !== false;
                const buttons = [];

                if (gallery?.isVideo) {
                    buttons.push({
                        title: "Choose from Phone",
                        iconName: "camera-outline",
                        iconType: "ionicon" as IconType,
                        onPress: () => {
                            isTransitioning.current = true;
                            bottomRef.current?.close();
                            openPicker(1);
                        }
                    });

                    if (gallery?.videoAvail) {
                        buttons.push({
                            title: "Choose from Store Gallery",
                            iconName: "images-outline",
                            iconType: "ionicon" as IconType,
                            onPress: () => {
                                isTransitioning.current = true;
                                bottomRef.current?.close();
                                setTimeout(() => {
                                    setStoreVisible(true);
                                }, 500);
                            }
                        });
                    }
                } else {
                    buttons.push({
                        title: "Take a Photo",
                        iconName: "camera-outline",
                        iconType: "ionicon" as IconType,
                        onPress: () => {
                            if (isStorageAllowed) {
                                isTransitioning.current = true;
                                bottomRef.current?.close();
                                PermissionServices.cameraAccessPermission().then(() => {
                                    openPicker(2);
                                }).catch((error) => console.log('CAMERA request error', error));
                            }
                        },
                        textStyle: !isStorageAllowed ? { color: '#94a3b8' } : undefined
                    });

                    buttons.push({
                        title: "Choose from Phone",
                        iconName: "images-outline",
                        iconType: "ionicon" as IconType,
                        onPress: () => {
                            if (isStorageAllowed) {
                                isTransitioning.current = true;
                                bottomRef.current?.close();
                                openPicker(1);
                            }
                        },
                        textStyle: !isStorageAllowed ? { color: '#94a3b8' } : undefined
                    });

                    if (gallery?.isStoreGallery) {
                        buttons.push({
                            title: "Choose from Store Gallery",
                            iconName: "upload-cloud",
                            iconType: "feather" as IconType,
                            onPress: () => {
                                if (isStorageAllowed) {
                                    isTransitioning.current = true;
                                    bottomRef.current?.close();
                                    setTimeout(() => {
                                        setStoreVisible(true);
                                    }, 500);
                                }
                            },
                            textStyle: !isStorageAllowed ? { color: '#94a3b8' } : undefined
                        });
                    }

                    if (gallery?.isRemoveProfile) {
                        buttons.push({
                            title: "Remove Image",
                            iconName: "trash-outline",
                            iconType: "ionicon" as IconType,
                            onPress: () => {
                                if (isStorageAllowed) {
                                    isTransitioning.current = true;
                                    bottomRef.current?.close();
                                    propFn?.('RemoveProfileImage');
                                }
                            },
                            textStyle: !isStorageAllowed ? { color: '#94a3b8' } : undefined
                        });
                    }
                }

                const buttonData: RBSheetDataInterface = {
                    headerTitle: gallery?.isVideo ? "Upload video" : "Upload image",
                    button: buttons,
                };

                const buttonsHeight = buttons.length === 1 ? 115 : buttons.length === 2 ? 160 : buttons.length === 3 ? 220 : 260;

                return (
                    <>
                        <ZenRBSheet
                            ref={bottomRef}
                            data={buttonData}
                            height={buttonsHeight}
                            onClose={() => {
                                if (isTransitioning.current) {
                                    isTransitioning.current = false;
                                } else {
                                    propFn?.('Cancel');
                                }
                            }}
                        />

                        {/* Store Image Gallery Modal Selection */}
                        {storeVisible && (
                            <CustomModal
                                animationType='slide'
                                visible={storeVisible}
                                onRequestClose={() => setStoreVisible(false)}
                            >
                                <Suspense fallback={
                                    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                                        <ZenLoader lottie={require("../../assets/infinityLoader.json")} lottieType="Local" />
                                    </View>
                                }>
                                    <StoreImageGallery
                                        isImagePicker={storeVisible}
                                        isImage={!gallery?.isVideo}
                                        setImageIsVisible={setStoreVisible}
                                        multi={!gallery?.isVideo && selectedIndex?.value?.imagesMaxLength ? selectedIndex?.value?.imagesMaxLength : 'single'}
                                        type={gallery?.screenName ?? gallery?.sectionName ?? 'Products'}
                                        onCloseStoreImageGallery={(result: any[]) => onCloseGallery(result, true)}
                                    />
                                </Suspense>
                            </CustomModal>
                        )}

                        {/* Preview and Uploading Modals */}
                        {((imageData?.length && showPreview) || load) && (
                            <Modal
                                statusBarTranslucent
                                navigationBarTranslucent
                                transparent={true}
                                onRequestClose={() => {
                                    imageData.length > 0 && setImageData([]);
                                    setShowPreview(false);
                                    setIndex(0);
                                    propFn?.('Cancel');
                                }}
                                animationType="slide"
                                visible={!!((imageData?.length && showPreview) || load)}
                            >
                                <View style={commonPickerStyle.transparentView}>
                                    {(imageData?.length && showPreview) ? (
                                        <View style={[commonPickerStyle.mainView]}>
                                            <View style={[commonPickerStyle.popoverView, { width: width > 600 ? '75%' : '90%', height: height * 0.75 }]}>
                                                {/* Header */}
                                                <View style={commonPickerStyle.headerView}>
                                                    <View style={commonPickerStyle.headerSubView}>
                                                        <View style={commonPickerStyle.headerIconView}>
                                                            <ZenIcon type="material" name="photo-library" size={24} color={theme.primary} />
                                                        </View>
                                                        <View>
                                                            <ZenText content={gallery?.isVideo ? "Video Preview" : "Preview Images"} style={commonPickerStyle.headerMainTite} bold />
                                                            {imageData?.length > 1 && <ZenText content={`${imageData?.length} Images Selected`} style={commonPickerStyle.headerSubTitle} />}
                                                        </View>
                                                    </View>
                                                    <ZenIcon type="ionicon" name="close" size={width > 600 ? 32 : 28} onPress={() => { setImageData([]); setShowPreview(false); setIndex(0); propFn?.('Cancel'); }} />
                                                </View>
                                                <ZenDivider style={{ backgroundColor: theme.borderColor, height: 1 }} />
                                                <ScrollView contentContainerStyle={{ paddingVertical: 10 }} >
                                                    {/* Content (Image or Video) */}
                                                    {gallery?.isVideo ? (
                                                        <View style={{ width: '100%', height: width > 600 ? 300 : 250, backgroundColor: '#f8fafc', borderRadius: 12, overflow: 'hidden' }}>
                                                            <Video
                                                                source={{ uri: imageData[index ?? 0]?.uri }}
                                                                style={{ width: '100%', height: '100%' }}
                                                                controls={true}
                                                                resizeMode="contain"
                                                            />
                                                        </View>
                                                    ) : (
                                                        <View style={{ width: '100%', aspectRatio: width > 600 ? 16 / 9 : 4 / 3, backgroundColor: '#f8fafc', borderRadius: 12, overflow: 'hidden', justifyContent: 'center', alignItems: 'center' }}>
                                                            <ImageBackground source={{ uri: imageData[index ?? 0]?.uri }} style={{ width: '100%', height: '100%' }} resizeMode='contain'>
                                                                {imageData?.length > 1 && (
                                                                    <View style={commonPickerStyle.countView}>
                                                                        <ZenText content={`${index + 1} / ${imageData?.length}`} style={commonPickerStyle.countText} />
                                                                    </View>
                                                                )}
                                                            </ImageBackground>
                                                        </View>
                                                    )}
                                                    <ZenDivider style={{ backgroundColor: theme.borderColor, height: 1, marginTop: 15 }} />

                                                    {imageData?.length > 1 ? (
                                                        <>
                                                            <ScrollView overScrollMode="never" horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={commonPickerStyle.scroll}>
                                                                {imageData?.map((item, i) => (
                                                                    <Pressable key={i} onPress={() => setIndex(i)}>
                                                                        <ZenIcon
                                                                            type="material-community"
                                                                            name='close-circle'
                                                                            color={theme.darkRed || '#b91c1c'}
                                                                            size={21}
                                                                            style={commonPickerStyle.closeIcon}
                                                                            onPress={() => {
                                                                                if (index === i) {
                                                                                    setIndex(0);
                                                                                }
                                                                                setImageData(prev => prev?.filter((_, key) => key !== i));
                                                                            }}
                                                                        />
                                                                        <View>
                                                                            <Image
                                                                                style={[commonPickerStyle.imageStyle, { borderColor: index === i ? theme.primary : theme.borderColor, borderWidth: index === i ? 2 : 1 }]}
                                                                                resizeMode='cover'
                                                                                source={{ uri: item?.uri }}
                                                                            />
                                                                            {!isFileValid(item) && (
                                                                                <View style={{
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    bottom: 0,
                                                                                    left: 0,
                                                                                    right: 0,
                                                                                    backgroundColor: 'rgba(0,0,0,0.4)',
                                                                                    justifyContent: 'center',
                                                                                    alignItems: 'center',
                                                                                    borderRadius: 8,
                                                                                }}>
                                                                                    <ZenIcon type="foundation" name="alert" size={26} color={theme.warning || '#f59e0b'} />
                                                                                </View>
                                                                            )}
                                                                        </View>
                                                                    </Pressable>
                                                                ))}
                                                            </ScrollView>
                                                            <ZenDivider style={{ backgroundColor: theme.borderColor, height: 1 }} />
                                                        </>
                                                    ) : null}

                                                    {/* File details */}
                                                    <View style={[commonPickerStyle.detailsView, { flexDirection: 'row', alignItems: 'center', gap: 12 }]}>
                                                        <View style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' }}>
                                                            <ZenIcon type="feather" name="file" size={20} color={theme.lightDark || '#334155'} />
                                                        </View>
                                                        <View style={{ flex: 1 }}>
                                                            <ZenText content={imageData[index]?.fileName || ""} style={commonPickerStyle.nameText} bold />
                                                            <ZenText content={`${(Number(imageData?.[index ?? 0]?.fileSize || 0) / Math.pow(1024, 2))?.toFixed(2)} MB  •  ${imageData[index ?? 0]?.width} x ${imageData[index ?? 0]?.height}`} style={commonPickerStyle.sizeText} />
                                                        </View>
                                                    </View>

                                                    {/* Validation error banner */}
                                                    {(!gallery?.isVideo && imageData?.some(i => !isFileValid(i))) && (
                                                        <View style={commonPickerStyle.errView}>
                                                            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8, paddingHorizontal: 4 }}>
                                                                <ZenIcon type="foundation" name="alert" size={18} color={'#dc2626'} style={{ marginTop: 2 }} />
                                                                <ZenText content="Validation Error: Unsupported file format or file size exceeded." style={[commonPickerStyle.bannerText, { color: '#991b1b', flex: 1, fontSize: 13 }]} />
                                                            </View>
                                                        </View>
                                                    )}
                                                </ScrollView>
                                                {/* Action Buttons */}
                                                <View style={commonPickerStyle.btnView}>
                                                    {(selectedIndex?.value?.imagesMaxLength && (selectedIndex?.value?.imagesMaxLength - imageData?.length) > 0 && !gallery?.isVideo) ? (
                                                        <LocalButton
                                                            style={{ flex: 1 }}
                                                            icon={<ZenIcon type="material-community" name="plus-circle" color={theme.primary} size={22} />}
                                                            type='Secondary'
                                                            title="Add More"
                                                            theme={theme}
                                                            onPress={() => openPicker(1)}
                                                        />
                                                    ) : null}
                                                    <LocalButton
                                                        loading={load}
                                                        disabled={!gallery?.isVideo && imageData?.some(i => !isFileValid(i))}
                                                        style={{ flex: 1 }}
                                                        icon={<ZenIcon type='material-community' name="cloud-upload" color={theme.light || '#ffffff'} size={22} />}
                                                        title="Upload"
                                                        theme={theme}
                                                        onPress={() => uploadImageInBucket(imageData, gallery?.isVideo)}
                                                    />
                                                </View>
                                            </View>
                                        </View>
                                    ) : load ? (
                                        <View style={commonPickerStyle.mainView}>
                                            <View style={{ padding: 30, borderRadius: 20, backgroundColor: 'rgba(255, 255, 255, 0.95)', alignItems: 'center', justifyContent: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 5 }}>
                                                <ActivityIndicator size='large' color={theme?.primary || '#005374'} />
                                                <ZenText content="Uploading..." style={[commonPickerStyle.loaderText, { color: theme?.lightDark || '#1e293b', marginTop: 15 }]} bold />
                                            </View>
                                        </View>
                                    ) : null}
                                </View>
                            </Modal>
                        )}
                    </>
                );
            }}
        </ThemeConsumer>
    );
};

const commonPickerStyle = StyleSheet.create({
    transparentView: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        justifyContent: 'flex-end'
    },
    mainView: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center'
    },
    popoverView: {
        backgroundColor: '#ffffff',
        borderRadius: 24,
        overflow: 'hidden',
        padding: 20,
        shadowColor: '#0f172a',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
        elevation: 8,
        borderWidth: 1,
        borderColor: '#f1f5f9'
    },
    headerView: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: 5,
        marginBottom: 12
    },
    headerSubView: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12
    },
    headerIconView: {
        padding: 10,
        borderRadius: 12,
        backgroundColor: 'rgba(0, 83, 116, 0.08)'
    },
    headerMainTite: {
        fontSize: appTypo.large,
        color: '#1e293b'
    },
    headerSubTitle: {
        fontSize: appTypo.xsmall,
        color: '#64748b',
        marginTop: 2
    },
    countView: {
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        borderRadius: 20,
        position: 'absolute',
        bottom: 12,
        right: 12
    },
    countText: {
        fontSize: appTypo.tiny,
        color: '#ffffff',
        paddingVertical: 6,
        paddingHorizontal: 12
    },
    scroll: {
        marginVertical: 16,
        gap: 12,
        marginHorizontal: 10,
        paddingRight: 20
    },
    closeIcon: {
        position: 'absolute',
        top: -6,
        right: -6,
        zIndex: 10,
        backgroundColor: '#ffffff',
        borderRadius: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.15,
        shadowRadius: 2,
        elevation: 2
    },
    imageStyle: {
        width: 72,
        height: 72,
        borderRadius: 10,
    },
    detailsView: {
        marginHorizontal: 10,
        marginVertical: 16,
    },
    nameText: {
        fontSize: appTypo.small,
        color: '#1e293b'
    },
    sizeText: {
        fontSize: appTypo.tiny,
        color: '#64748b',
        marginTop: 4
    },
    btnView: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        marginVertical: 8
    },
    loaderText: {
        fontSize: appTypo.regular,
        color: '#ffffff',
    },
    bannerView: {
        marginTop: 8,
        marginBottom: 16,
        padding: 10,
        backgroundColor: '#fffbeb',
        borderColor: '#fef3c7',
        borderWidth: 1,
        borderRadius: 12
    },
    bannerText: {
        textAlign: 'justify',
        fontSize: appTypo.small,
    },
    cancelText: {
        fontSize: 16,
        color: '#475569'
    },
    listView: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    listText: {
        fontSize: appTypo.regular,
        textAlign: 'left',
        alignSelf: 'center'
    },
    cancelView: {
        backgroundColor: '#f8fafc',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        borderRadius: 12,
        paddingVertical: 14,
        alignItems: 'center',
        marginTop: 10,
        marginBottom: 5
    },
    errView: {
        marginTop: 8,
        marginBottom: 16,
        padding: 12,
        backgroundColor: '#fef2f2',
        borderColor: '#fee2e2',
        borderWidth: 1,
        borderRadius: 12
    },
    sheetContainer: {
        width: '100%',
        paddingHorizontal: 20,
        paddingTop: 12,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        shadowColor: '#0f172a',
        shadowOffset: { width: 0, height: -8 },
        shadowOpacity: 0.08,
        shadowRadius: 16,
        elevation: 16,
    },
    sheetHandle: {
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#e2e8f0',
        alignSelf: 'center',
        marginBottom: 16
    },
    actionItem: {
        borderRadius: 14,
        paddingVertical: 14,
        paddingHorizontal: 16,
        marginVertical: 5,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: '#f1f5f9',
        shadowColor: '#0f172a',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.04,
        shadowRadius: 6,
        elevation: 2,
    },
    actionIconWrapper: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: '#f1f5f9',
        justifyContent: 'center',
        alignItems: 'center'
    },
});

export default CommonImageUpload;